/**
 * @typedef {'user' | 'company' | 'server' | 'public'} FeatureFlagType
 */

/**
 * @typedef {Object} FeatureFlagDefinition
 * @property {string} name - The name of the feature flag (in snake_case) that is stored in the database.
 * @property {FeatureFlagType} type - The type of entity this feature flag applies to.
 * @property {string} label - The human-readable label of the feature flag that is displayed in the UI.
 * @property {string} [description] - A description of the feature flag.
 * @property {boolean} [restricted] - Whether the feature flag can be enabled or disabled by clients.
 * @property {boolean} [private] - Whether the feature flag is visible only to clients who already have it (either enabled or disabled).
 */

/**
 * @type {FeatureFlagDefinition[]}
 */
export const FEATURE_FLAG_DEFINITIONS = [
  {
    name: 'cross_server_job_sharing',
    label: 'Cross-Server Job Sharing',
    type: 'company',
    description: 'Enables Admin Page menu to setup job sharing.',
    private: false,
    restricted: false
  },
  {
    name: 'cross_server_model_sharing',
    label: 'Cross-Server Model Sharing',
    type: 'company',
    description: 'Enables Admin Page menu to setup model sharing.',
    private: false,
    restricted: false
  },
  {
    name: 'server_access_sharing',
    label: 'Cross-Server Access Menu',
    type: 'company',
    description: 'Enables Admin Page menu to accept server access invites.',
    private: false,
    restricted: false
  },
  {
    name: 'add_feedback_attribute',
    label: 'Add Feedback Attribute',
    type: 'company',
    description:
      'Adds a "Feedback Users" attribute containing the email addresses of conversation members to an entity when a feedback issue is raised; the attribute is removed when the issue is resolved.',
    private: true,
    restricted: false
  },
  {
    name: 'address_tool_model_configurable',
    label: 'Configurable Address Tool',
    type: 'company',
    description: 'Enables Model Editor configuration to set custom address attributes.',
    private: true,
    restricted: false
  },
  {
    name: 'hide_attachment_record_export_on_admin',
    label: 'Hide Attachment Record Export on Admin',
    type: 'server',
    description: 'Disables the Attachment Record Export button for Applicants on the admin page.',
    private: true,
    restricted: false
  },
  {
    name: 'show_attachment_counts_export',
    label: 'Show Attachment Counts Export',
    type: 'server',
    description: 'Shows the Attachment Counts export option in Attachment Records.',
    private: true,
    restricted: false
  },
  {
    name: 'show_other_active_app_counts',
    label: 'Show Other Active App Counts',
    type: 'server',
    description:
      'Shows everyone on a pole application how many other active applications each pole is in, as a pole warning and a map label.',
    private: true,
    restricted: false
  },
  {
    name: 'workflow_email_make_ready_notification_companies',
    label: 'Workflow Email Make Ready Notification Companies',
    type: 'server',
    description:
      'Show option to send emails to Make Ready Notification Contacts on workflow emails. Reduction of list via subtraction of overlapping work from alternate designs is not supported.',
    private: false,
    restricted: false
  },
  {
    name: 'ai_calibration_bypass',
    label: 'Internal Calibration Bypass',
    type: 'company',
    description: 'All uploaded photos will be calibratable.',
    private: true,
    restricted: false
  },
  {
    name: 'canon_r7_calibration',
    label: 'Canon EOS R7 Calibration',
    type: 'company',
    description: 'Allows calibration with Canon EOS R7 photos using the RF24-105mm F4-7.1 IS STM lens.',
    private: true,
    restricted: true
  },
  {
    name: 'always_show_pci_attributes',
    label: 'Skip Calibration in PCI',
    type: 'company',
    description: 'Allows the user to skip transferring heights in the PCI Routine.',
    private: true,
    restricted: false
  },
  {
    name: 'annotated_photo_pole_tag',
    label: 'Annotated Photo Tag Text',
    type: 'company',
    description: 'Displays the pole owner tag on each annotated photo.',
    private: true,
    restricted: false
  },
  {
    name: 'annotation_label_shortnames_disabled',
    label: 'Disable Photo Annotation Label Short Names',
    type: 'server',
    description: 'Stops photo annotation labels from substituting picklist short names for trace data, company names, and wire specs.',
    private: false,
    restricted: false
  },
  {
    name: 'annotation_label_shortnames_disabled',
    label: 'Disable Photo Annotation Label Short Names',
    type: 'company',
    description: 'Stops photo annotation labels from substituting picklist short names for trace data, company names, and wire specs.',
    private: false,
    restricted: false
  },
  {
    name: 'assessment_drawing_tool_steps',
    label: 'Mobile Assessment Drawing Tools',
    type: 'company',
    description:
      'Allows users to create assessment steps that call existing drawing tools, allowing mobile users to draw on the map as a part of an assessment. Appears as an additional step type in the model editor.',
    private: true,
    restricted: false
  },
  {
    name: 'automated_cu_entry',
    label: 'CU Automations',
    type: 'company',
    description: 'Enables guided CU entry in CU View',
    private: true,
    restricted: false
  },
  {
    name: 'compatible_unit_editor',
    label: 'CU Editor',
    type: 'company',
    description: 'Enables Compatible Units in the Model Editor.',
    private: true,
    restricted: false
  },
  {
    name: 'configurable_exports',
    label: 'Configurable Exports',
    type: 'company',
    description: 'Enables Configurable Exports in the Model Editor.',
    private: true,
    restricted: false
  },
  {
    name: 'construction_specs',
    label: 'Construction Specs',
    type: 'company',
    description: 'Enables Construction Specs in the Model Editor.',
    private: false,
    restricted: false
  },
  {
    name: 'custom_logic_routine_values',
    label: 'Photo Routine Custom Logic',
    type: 'company',
    description: 'Enables custom logic to generate values for attributes in Photo Routines.',
    private: true,
    restricted: false
  },
  {
    name: 'google_drive_access',
    label: 'Google Drive Integration',
    type: 'company',
    description: 'Enables Google Drive file integrations.',
    private: true,
    restricted: false
  },
  {
    name: 'hub_submit_to_portal',
    label: 'Submit to Portal',
    type: 'company',
    description: 'Deprecated feature that allows applications to be submitted to the Portal from the Map page.',
    private: true,
    restricted: false
  },
  {
    name: 'hub_submit_workflow_holds',
    label: 'Project Holds',
    type: 'company',
    description: 'Allows Holds to be added to Projects from the Map page.',
    private: true,
    restricted: false
  },
  {
    name: 'map_print_rotation',
    label: 'Map Print Rotation',
    type: 'company',
    description: 'Allows Map Prints contents to be rotated.',
    private: true,
    restricted: false
  },
  {
    name: 'map_print_dpi_change',
    label: 'Map Print DPI Change',
    type: 'company',
    description: 'Changes the map print DPI from our standard 100 to the 72 that is standard for PDFs.',
    private: true,
    restricted: false
  },
  {
    name: 'map_print_custom_computed_annotations',
    label: 'Computed Annotations Editor',
    type: 'company',
    description: 'Enables a custom logic editor for Computed Annotations in Map Print mode.',
    private: false,
    restricted: false
  },
  {
    name: 'master_location_directory_sharing',
    label: 'Master Location Directory Sharing',
    type: 'company',
    description: 'Allows Master Location Directories to be shared from the Admin page.',
    private: false,
    restricted: false
  },
  {
    name: 'new_pole_design',
    label: 'New Pole Designer',
    type: 'company',
    description: 'Enables New Pole Designer for poles without existing height photos.',
    private: false,
    restricted: false
  },
  {
    name: 'log_gps_to_job',
    label: 'Log GPS To Job',
    type: 'company',
    description: 'Enables the mobile setting to log device GPS points to the active job.',
    private: true,
    restricted: false
  },
  {
    name: 'pci_midspans',
    label: 'PCI Midspans',
    type: 'company',
    description: 'Enables Midspan collection using the PCI workflow.',
    private: true,
    restricted: true
  },
  {
    name: 'pole_replacement_dialog',
    label: 'Configurable Pole Replacement Tool',
    type: 'company',
    description: 'Replaces the Pole Replacement tool with a configurable version.',
    private: true,
    restricted: false
  },
  {
    name: 'transfer_heights_button',
    label: 'Transfer Markers to Photo',
    type: 'company',
    description:
      'Adds a button in the photo chooser to transfer markers from the existing main photo to a new photo using known points in both photos.',
    private: true,
    restricted: false
  },
  {
    name: 'web_automations',
    label: 'Web Automations',
    type: 'company',
    description: 'Enables Web Automations in the Model Editor. Requires Web Automations browser extension.',
    private: true,
    restricted: true
  },
  {
    name: 'legacy_portal_contact_button',
    label: 'Legacy Portal Contact Button',
    type: 'server',
    description: 'Shows the legacy "Contact" button in the Portal.',
    private: true,
    restricted: false
  },
  {
    name: 'legacy_cancel_application_button',
    label: 'Legacy Portal Cancel Application Button',
    type: 'server',
    description: 'Shows the legacy "Cancel Application" button in the Portal.',
    private: true,
    restricted: false
  },
  {
    name: 'legacy_delete_draft_button',
    label: 'Legacy Portal Delete Draft Button',
    type: 'server',
    description: 'Shows the legacy "Delete Draft" button in the Portal.',
    private: true,
    restricted: false
  },
  {
    name: 'auto_insert_insulator_order',
    label: 'Auto Insert Insulator Order',
    type: 'company',
    description: 'Enables Calc Bearing tool to automatically insert the Insulator Order attribute on insulators on arms.',
    private: true,
    restricted: true
  },
  {
    name: 'finished_app_edit_warning',
    label: 'Finished App Edit Warning',
    type: 'company',
    description: 'Generates a warning when trying to unlock a completed application.',
    private: true,
    restricted: true
  },
  {
    name: 'legacy_reference_layer_importer',
    label: 'Legacy Reference Layer Importer',
    type: 'company',
    description: 'Enable the legacy reference layer importer in the Model Editor.',
    private: true,
    restricted: true
  },
  {
    name: 'portal_server_access_invite_only',
    label: 'Portal Server Access Invite-Only',
    type: 'public',
    description: 'Restricts Portal Server Access to only those who have been invited.',
    private: true,
    restricted: true
  },
  {
    name: 'model_info_dialog',
    label: 'Model Info Dialog',
    type: 'user',
    description: 'Adds a button to model editor that opens a dialog with information about the currently-viewed model.',
    private: true,
    restricted: false
  },
  {
    name: 'streetlight_ground_verify_custom_wording',
    label: 'Streetlight Ground Verify Custom Wording',
    type: 'server',
    description: 'Enable custom wording for MR Note when "gl" is used on a streetlight. "Verify Streetlight is Grounded."',
    private: true,
    restricted: true
  },
  {
    name: 'user_last_edited_job_data_export',
    label: 'User Last Edited Job Data Export',
    type: 'company',
    description:
      'Adds a button to /admin that lists all users with write permission or a license and includes the date of the last time they edited an attribute or moved something on the map of any job.',
    private: true,
    restricted: false
  },
  {
    name: 'fielder_quality_dashboard_widget',
    label: 'Fielder Quality Dashboard Widget',
    type: 'company',
    description: 'Enables a custom Home widget, Action Tracking, and Dashboards config to measure field data quality.',
    private: false,
    restricted: false
  },
  {
    name: 'one_percent_sag_view',
    label: 'Davey One Percent Sag View',
    type: 'company',
    description: 'Allows users to view 1% ice sag values for selected proposed cables in a dialog window on the midspan photo.',
    private: true,
    restricted: false
  },
  {
    name: 'njuns_integration',
    label: 'NJUNS Integration',
    type: 'company',
    description: 'Enables a configurable API layer that shows NJUNS tickets on the Map.',
    private: true,
    restricted: false
  },
  {
    name: 'njuns_reference_misc_id_attribute_mappings',
    label: 'NJUNS Reference/Misc ID Attribute Mappings',
    type: 'company',
    description: 'Prefers and maps NJUNS Reference ID and Misc ID from designated job-level attributes.',
    private: true,
    restricted: false
  },
  {
    name: 'filter_shp_file_by_saved_view',
    label: 'Filter SHP File by Saved View',
    type: 'company',
    description: 'Enables an option in the Export Manager for filtering nodes and connections in SHP files based on saved views.',
    private: true,
    restricted: false
  },
  {
    name: 'legacy_portal_application_write_permission',
    label: 'Legacy Portal Application Write Permission',
    type: 'server',
    description: 'Allows users without write permission or the application manager role to create and progress applications in the Portal.',
    private: true,
    restricted: true
  },
  {
    name: 'use_pole_tags_for_sections',
    label: 'Use Pole Tags for Annotated Make Ready Photo Sections',
    type: 'company',
    description:
      'Annotated Make Ready Photos will use pole tags rather than scids in the file name to represent the nodes a section is between.',
    private: true,
    restricted: false
  },
  {
    name: 'job_archive_tracking',
    label: 'Job Archive Tracking',
    type: 'company',
    description: 'Emails admins when and by whom jobs are archived.',
    private: true,
    restricted: false
  },
  {
    name: 'custom_3d_model',
    label: 'Custom 3D Models',
    type: 'company',
    description: 'Enables uploading custom equipment and insulator models for 3D View in the Model Editor.',
    private: true,
    restricted: false
  },
  {
    name: 'remove_scrape_photo_qc',
    label: 'Remove Scrape Photo QC',
    type: 'company',
    description: 'Allows users to skip the Scrape Photo Data tool QC dialog.',
    private: false,
    restricted: false
  },
  {
    name: 'user_option_disable_section_info_windows',
    label: 'User Option to Disable Section Info Windows',
    type: 'company',
    description:
      'Adds an option to user account settings that disables trace info bubbles that show up on adjacent sections when clicking a marker in a height photo.',
    private: true,
    restricted: false
  },
  {
    name: 'pla_excel_export',
    label: 'PLA Excel Export',
    type: 'company',
    description: 'Adds an Excel version of the PLA export to the Download Manager.',
    private: true,
    restricted: false
  },
  {
    name: 'persistent_folder_sharing',
    label: 'Persistent Folder Sharing',
    type: 'company',
    description: 'Adds the ability to set up persistent sharing on project folders.',
    private: true,
    restricted: false
  },
  {
    name: 'legacy_submission_limits',
    label: 'Legacy Submission Limits',
    type: 'company',
    description: 'Enables legacy Pole Limit setting under User Options instead of configurable Submission Windows.',
    private: true,
    restricted: true
  },
  {
    name: 'show_calibration_line_in_thumbnails',
    label: 'Show Calibration Line in Thumbnails',
    type: 'company',
    description: 'Displays the yellow calibration line in all photo thumbnails.',
    private: false,
    restricted: false
  },
  {
    name: 'hide_applicant_pole_limit_and_count',
    label: 'Hide Applicant Pole Limit and Count',
    type: 'server',
    description: 'Applicants do not see their current submission limit or pole count on the Pole Application page.',
    private: true,
    restricted: false
  },
  {
    name: 'enable_forms',
    label: 'Enable Forms',
    type: 'company',
    description: 'Enable Forms when starting Workflows.',
    private: true,
    restricted: true
  },
  {
    name: 'ignore_legacy_attachment_types_in_exports',
    label: 'Ignore Legacy Attachment Types in Exports',
    type: 'server',
    description:
      'When enabled, app exports will use the attachment types defined in the model rather than a hardcoded list of legacy attachment types.',
    private: true,
    restricted: true
  },
  {
    name: 'uplift_calculator',
    label: 'Uplift Calculator (Beta)',
    type: 'company',
    description: 'Enables an additional menu for Pole Replacements to calculate uplift.',
    private: true,
    restricted: false
  },
  {
    name: 'add_rod_size_to_down_guy_mr_note',
    label: 'Add Rod Size to Down Guy MR Note',
    type: 'company',
    description:
      'Adds anchor rod size to generated down guy make ready notes as well as using the Sizes Of Attached Dn Guys attribute on the anchor if no down guy spec is selected.',
    private: true,
    restricted: true
  },
  {
    name: 'monday_integration',
    type: 'company',
    label: 'Monday.com Dashboards',
    description: 'Enables the creation of Monday.com dashboards on the Project Management page.',
    private: true,
    restricted: false
  },
  {
    name: 'workflow_management_early_access',
    type: 'company',
    label: 'Workflow Management Early Access',
    description: 'Enables Workflow configurations in the Model Editor.',
    private: false,
    restricted: false
  },
  {
    name: 'timesheet_manager_can_change_user_hours',
    type: 'user',
    label: 'Timesheet Manager Can Change User Hours',
    description: 'Allows timesheet managers to change the hours of other users from the timesheet widget.',
    private: true,
    restricted: true
  },
  {
    name: 'adjust_proposed_midspan_cable_based_on_insertion_logic_option',
    type: 'company',
    label: 'Adjust Proposed Midspan Cable Based On Insertion Logic Option',
    description:
      'Adds a Model Editor option to augment the Auto Adjust feature for proposed cables in the midspan. When this option is turned on, proposed midspan cables will reset based on the selected insertion logic of the model when a marker on the same trace is moved on an adjacent pole.',
    private: true,
    restricted: false
  },
  {
    name: 'hide_modern_attachment_records',
    type: 'server',
    label: 'Hide Modern Attachment Records',
    description: 'Hides modern attachment record interfaces from Pole Application, Map, and Search.',
    private: true,
    restricted: false
  },
  {
    name: 'enter_crossarm_insulator_offsets',
    type: 'company',
    label: 'Enter Crossarm Insulator Offsets',
    description: 'Allows users to enter insulator offsets on crossarms during the Hardware Details routine.',
    private: true,
    restricted: false
  },
  {
    name: 'show_legacy_attachment_records',
    type: 'server',
    label: 'Show Legacy Attachment Records',
    description: 'Show legacy attachment records in Pole Application and Map info windows.',
    private: true,
    restricted: false
  },
  {
    name: 'target_detection_disabled',
    label: 'Disable Target Detection',
    type: 'server',
    description: 'Prevents users from snapping Calibration Anchors using Target Detection.',
    private: false,
    restricted: false
  },
  {
    name: 'target_detection_disabled',
    label: 'Disable Target Detection',
    type: 'company',
    description: 'Prevents users from snapping Calibration Anchors using Target Detection.',
    private: false,
    restricted: false
  },
  {
    name: 'model_editor_citations',
    label: 'Model Editor Citations',
    type: 'company',
    description: 'Enable citations in the Model Editor.',
    private: true,
    restricted: false
  },
  {
    name: 'can_manage_user_attributes',
    label: 'Enable User Attributes',
    type: 'company',
    description: "User attributes can be created in your company's default model, and added to users on the Admin page.",
    private: false,
    restricted: false
  },
  {
    name: 'deployment_maps_per_review_contractor',
    label: 'Deployment Maps Per Review Contractor',
    type: 'server',
    description: 'Shows configuration settings for deployment maps per review contractor in the Workflow User Options config.',
    private: true,
    restricted: false
  },
  {
    name: 'draft_number_column_in_poles_export',
    label: 'Draft Number Column in Poles Export',
    type: 'server',
    description: 'Enable a draft number column as the first column in the poles export.',
    private: true,
    restricted: false
  },
  {
    name: 'worst_case_sag_height_photo_button',
    label: 'Worst Case Sag Height Photo Button',
    type: 'company',
    description: 'Enable the Worst Case Sag button on height photos.',
    private: true,
    restricted: true
  },
  {
    name: 'nodes_sections_connections_export_use_attribute_sets',
    label: 'Use Attribute Sets With The Nodes Sections Connections Export',
    type: 'company',
    description: 'Enabled the option to choose an attribute set when running the export to control the attributes included in the export.',
    private: true,
    restricted: false
  },
  {
    name: 'nodes_sections_connections_export_include_node_ids_for_sections',
    label: 'Include Node IDs In Sections Sheet In Nodes Sections Connections Export',
    type: 'company',
    description: 'Includes IDs for the node endpoints for the connection that the section belongs to in the sections sheet in the export.',
    private: true,
    restricted: false
  },
  {
    name: 'authenticated_api_layers',
    label: 'Authenticated API Layers',
    type: 'server',
    description: 'Enables the use of password-protected API layers in the Model Editor.',
    private: true
  },
  {
    name: 'include_section_gis_shapefile',
    label: 'Include Section GIS Shapefile',
    type: 'company',
    description:
      'Includes Section GIS Shapefile option in the Export Manager for a version of the shapefile export that only shows span sections.',
    private: true,
    restricted: false
  },
  {
    name: 'custom_photo_form_textbox',
    label: 'Custom Photo Form Text Box',
    type: 'company',
    description: 'Enables extra text box in the photo form',
    private: true
  },
  {
    name: 'show_job_layer_labels',
    label: 'Show Labels on Job Layers',
    type: 'company',
    description: 'Shows your active labels on nodes and connections in job layers on the map.',
    private: true,
    restricted: true
  },
  {
    name: 'create_nodes_on_photo_upload',
    label: 'Create Nodes on Upload',
    type: 'company',
    description:
      'Enables an option on the Upload page to create nodes wherever photos were taken. Requires valid latitude and longitude in photo EXIF data.',
    private: true,
    restricted: false
  },
  {
    name: 'hide_bulk_archive_jobs_feature',
    label: 'Hide Bulk Archive Jobs Feature',
    type: 'company',
    description: 'Hides the bulk archive jobs right-click option in the project chooser.',
    private: false,
    restricted: false
  },
  // TODO: Enable this and remove 'enable_new_map_engine' when cesium is more feature complete
  // Any code with 'enable_new_map_engine' can be swapped to 'disable_new_map_engine' at that point
  // {
  //   name: 'disable_new_map_engine',
  //   label: 'Disable New Map Engine',
  //   type: 'company',
  //   description: 'Forces legacy map engine.',
  //   private: true
  // },
  {
    name: 'enable_new_map_engine',
    label: 'Enable New Map Engine',
    type: 'user',
    description: 'Enables the new map engine.',
    private: true,
    restricted: false
  },
  {
    name: 'name_photo_export_by_custom_logic',
    label: 'Custom Photo Export Naming and Sort Order',
    type: 'company',
    description: 'Enables custom logic and a configurable classification sort order for naming photo exports.',
    private: false,
    restricted: false
  },
  {
    name: 'include_spida_photos',
    label: 'Include Photos in SPIDA Export',
    type: 'company',
    description: 'Enables options to include photos and annotated make ready photos in SpidaCalc project file exports.',
    private: false,
    restricted: false
  },
  {
    name: 'can_grant_extended_api_access',
    label: 'Can Grant Extended API Access',
    type: 'company',
    description: 'Allows a user to grant extended API access to other users.',
    private: true,
    restricted: true
  },
  {
    name: 'enable_api_user_state_calls',
    label: 'Enable API User State Calls',
    type: 'company',
    description: 'Allows a user to call the API to get/set the state of other users.',
    private: true,
    restricted: false
  },
  {
    name: 'mobile_only_role',
    label: 'Restricted Mobile User',
    type: 'company',
    description: 'Adds a role on the Admin Page menu to restrict users to certain pages (Admin, Mobile, and Upload).',
    private: false,
    restricted: false
  },
  {
    name: 'api_usage_limit_alert_emails',
    label: 'API Usage Limit Alert Emails',
    type: 'company',
    description:
      'Sends company admins an email once monthly API token usage (across v2 and v3 API requests) crosses certain thresholds (percentages) of the license-based allotment.',
    private: true,
    restricted: false
  },
  {
    name: 'phase_tracking_watch_metadata',
    label: '[Legacy] Metadata Event-Driven Phase Tracking',
    type: 'server',
    description: 'Enables legacy metadata event-driven phase tracking.',
    private: true,
    restricted: true
  },
  {
    name: 'timeline_tracking',
    label: 'Timeline Tracking',
    type: 'server',
    description: 'Enables timeline config-driven tracking.',
    private: true,
    restricted: true
  },
  {
    name: 'configurable_sag_tables',
    label: 'Configurable Sag Tables',
    type: 'company',
    description: 'Enables the Sag Table section on Model Editor wire specs.',
    private: true,
    restricted: false
  },
  {
    name: 'configurable_mr_directives',
    label: 'Configurable MR Directives',
    type: 'company',
    description: 'Enables configurable make-ready directives to replace hardcoded ones.',
    private: true,
    restricted: true
  },
  {
    name: 'external_api_call_workflow_action',
    label: 'External API Call Workflow Action',
    type: 'server',
    description: 'Enables the Call External API action in workflow configurations.',
    private: true,
    restricted: false
  },
  {
    name: 'map_print_svg_templates',
    label: 'Map Print SVG Templates',
    type: 'company',
    description: 'Show option in Map Print toolbar to use SVG mask templates for Map Prints.',
    private: true,
    restricted: false
  },
  {
    name: 'master_location_directory_check_in_admins_only',
    type: 'company',
    label: 'Master Location Directory Check In Admins Only',
    description: 'Checking jobs into a Master Location Directory is restricted to admins.',
    private: false,
    restricted: false
  }
];
